import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { flashcards, Flashcard } from '../data/flashcards';
import { getFlashcards } from '../data/flashcardsProvider';
import FlashcardCard from '../components/FlashcardCard';
import ProgressBar from '../components/ProgressBar';
import Stats from '../components/Stats';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { supabaseHelpers, StudySession, supabase } from '../lib/supabase';
import SubscriptionModal from '../components/SubscriptionModal';

const FLASH_TOUR_STEPS: { id: string; title: string; body: string }[] = [
  { id: 'flash-welcome', title: 'Study session', body: 'You\'re in a flashcard study session. Here\'s how to use it.' },
  { id: 'flash-progress', title: 'Session progress', body: 'This bar shows how far you are in this session (e.g. card 5 of 50).' },
  { id: 'flash-card', title: 'The flashcard', body: 'Tap the card to flip it and see the answer. You can also swipe left (don\'t know) or right (know it) to answer without using the buttons.' },
  { id: 'flash-buttons', title: 'Answer buttons', body: '' },
  { id: 'flash-stats', title: 'Session stats', body: 'Correct, incorrect, total, and streak for this session.' },
];

/** For free users: take at most `perCategory` cards from each category, then shuffle. */
function limitToFivePerCategory(cards: Flashcard[], perCategory: number): Flashcard[] {
  const byCategory: Record<string, Flashcard[]> = {};
  for (const card of cards) {
    const cat = card.category || 'Other';
    if (!byCategory[cat]) byCategory[cat] = [];
    if (byCategory[cat].length < perCategory) byCategory[cat].push(card);
  }
  return Object.values(byCategory).flat();
}

interface FlashcardAppProps {
  mode: 'demo' | 'full';
}

const FlashcardApp: React.FC<FlashcardAppProps> = ({ mode }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [stats, setStats] = useState({
    correct: 0,
    incorrect: 0,
    total: 0,
    streak: 0,
    maxStreak: 0
  });
  const [shuffledCards, setShuffledCards] = useState<Flashcard[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerDirection, setBannerDirection] = useState<'left' | 'right' | null>(null);

  const [isProcessingSwipe, setIsProcessingSwipe] = useState(false);

  const [currentSession, setCurrentSession] = useState<StudySession | null>(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [hasInteractedWithFirstCard, setHasInteractedWithFirstCard] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourRect, setTourRect] = useState<DOMRect | null>(null);
  const tourOverlayRef = useRef<HTMLDivElement>(null);
  
  // Get current card
  const currentCard = shuffledCards[currentIndex];
  const { user, userProfile, userStats, updateUserStats, darkMode, setDarkMode } = useAuth();
  const darkModeBeforeDemo = useRef(darkMode);

  // The demo opens in dark mode by default without changing the visitor's site-wide preference.
  useLayoutEffect(() => {
    if (mode !== 'demo') return;
    const previous = darkModeBeforeDemo.current;
    setDarkMode(localStorage.getItem('demoDarkMode') !== 'false');
    return () => setDarkMode(previous);
  }, [mode, setDarkMode]);
  
  // Use user's profile state directly
  const currentState = userProfile?.state?.toLowerCase() || 'ga'; // Default to GA if no state is set
  const navigate = useNavigate();
  const cardRef = useRef<HTMLDivElement>(null);

  const isFreeAccount = mode === 'full' && userProfile?.subscription_type === 'free';
  const FREE_CARDS_PER_CATEGORY = 5;

  // Get session configuration from URL params
  const sessionConfig = useMemo(() => {
    const type = (searchParams.get('type') || 'all') as 'all' | 'category' | 'bookmarked' | 'weak' | 'random';
    
    // For bookmarked and weak sessions, don't use a default card limit
    const cardLimit = type === 'bookmarked' || type === 'weak' 
      ? Number(searchParams.get('cardLimit')) || 0  // Use 0 for no limit
      : Number(searchParams.get('cardLimit')) || 50; // Use 50 as default for other types
    
    const config = {
      type,
      cardLimit,
      category: searchParams.get('category') ? decodeURIComponent(searchParams.get('category')!) : 'all',
      randomCount: Number(searchParams.get('randomCount')) || 0,
      resume: searchParams.get('resume') || null
    };

    return config;
  }, [searchParams]);

  // Determine card limit based on mode and session configuration
  const cardLimit = useMemo(() => {

    
    if (mode === 'demo') {

      return 10;
    }
    
    // For random study sessions, use randomCount as the limit
    if (sessionConfig.type === 'random' && sessionConfig.randomCount > 0) {

      return sessionConfig.randomCount;
    }
    
    // For bookmarked and weak card sessions, use the actual number of cards in the session
    if (sessionConfig.type === 'bookmarked' || sessionConfig.type === 'weak') {
      const actualCardCount = shuffledCards.length;
      
      // If we don't have shuffled cards yet, return 0 to prevent showing wrong count
      if (actualCardCount === 0) {
        return 0;
      }
      return actualCardCount;
    }
    
    // For category sessions, use the actual number of cards in that category
    if (sessionConfig.type === 'category') {
      const actualCardCount = shuffledCards.length;
      
      // If we don't have shuffled cards yet, return 0 to prevent showing wrong count
      if (actualCardCount === 0) {
        return 0;
      }
      return actualCardCount;
    }
    
    // For 'all' sessions, use the total number of flashcards available
    if (sessionConfig.type === 'all') {
      const totalCardCount = flashcards.length;

      return totalCardCount;
    }
    
    // For other session types, use cardLimit from config
    const effectiveCardLimit = sessionConfig.cardLimit > 0 ? sessionConfig.cardLimit : 50;

    return effectiveCardLimit;
  }, [mode, sessionConfig, shuffledCards.length]);

  // Initialize or resume study session
  useEffect(() => {
    const initializeSession = async () => {
      const stateCards = getFlashcards(currentState as any);
      // Set a timeout to prevent infinite loading
      const loadingTimeout = setTimeout(() => {
        console.warn('Loading timeout reached, falling back to basic session');
        setIsLoadingCards(false);
        
        // Create a basic fallback session
        const fallbackCards = stateCards.slice(0, 50);
        const shuffled = [...fallbackCards].sort(() => Math.random() - 0.5);
        setShuffledCards(shuffled);
        setCurrentIndex(0);
        setStats({
          correct: 0,
          incorrect: 0,
          total: 0,
          streak: 0,
          maxStreak: 0
        });
      }, 10000); // 10 second timeout

      try {
        // For demo mode, initialize with demo cards
        if (mode === 'demo') {

          const demoCards = stateCards.slice(0, 10);
          const shuffled = [...demoCards].sort(() => Math.random() - 0.5);
          setShuffledCards(shuffled);
          setIsLoadingCards(false);
          clearTimeout(loadingTimeout);
          return;
        }
        
        if (!user) {
          clearTimeout(loadingTimeout);
          return;
        }


        // For weak, bookmarked, category, all, and random sessions, always deactivate any existing sessions first
        if (sessionConfig.type === 'weak' || sessionConfig.type === 'bookmarked' || sessionConfig.type === 'category' || sessionConfig.type === 'all' || sessionConfig.type === 'random') {
          const activeSession = await supabaseHelpers.getActiveStudySession(user.id);
          if (activeSession) {

            await supabaseHelpers.deactivateStudySession(activeSession.id);
          }
        } else {
          // For other session types, check for active session and resume if same type
          const activeSession = await supabaseHelpers.getActiveStudySession(user.id);
          if (activeSession) {

            
            if (activeSession.session_type !== sessionConfig.type) {
  
              await supabaseHelpers.deactivateStudySession(activeSession.id);
            } else {
              setCurrentSession(activeSession);
              setCurrentIndex(activeSession.current_index);
              setStats(activeSession.stats);
              
              // Convert card IDs back to full card objects
              const sessionCards = activeSession.shuffled_cards.map((cardId: number) => 
                flashcards.find(card => card.id === cardId)
              ).filter(Boolean) as Flashcard[];
              setShuffledCards(sessionCards);
              return;
            }
          }
        }

        // Check if resuming a specific session (for resume button)
        if (sessionConfig.resume) {
          const session = await supabaseHelpers.getActiveStudySession(user.id);
          if (session && session.id === sessionConfig.resume) {

            setCurrentSession(session);
            setCurrentIndex(session.current_index);
            setStats(session.stats);
            
            // Convert card IDs back to full card objects
            const sessionCards = session.shuffled_cards.map((cardId: number) => 
              flashcards.find(card => card.id === cardId)
            ).filter(Boolean) as Flashcard[];
            setShuffledCards(sessionCards);
            return;
          }
        }

        // Create new session based on configuration
        let filteredCards = stateCards;
        
        // Filter cards based on session type
        switch (sessionConfig.type) {
          case 'bookmarked':

            try {
              const bookmarkedIds = await supabaseHelpers.getBookmarkedCards(user.id);


              // Show some sample flashcards to verify the data

              
              filteredCards = stateCards.filter(card => bookmarkedIds.includes(card.id));

              if (filteredCards.length === 0) {


              } else {

              }
            } catch (error) {
              console.error('Error getting bookmarked cards:', error);

              filteredCards = flashcards;
            }
            break;
          case 'weak':

            try {
              const weakCardIds = await supabaseHelpers.getWeakCards(user.id);


              // Show some sample flashcards to verify the data

              
              filteredCards = stateCards.filter(card => weakCardIds.includes(card.id));

              if (filteredCards.length === 0) {

              } else {

              }
              
              // Test: Check a few specific cards to see their progress
              if (weakCardIds.length > 0) {
                const testCardId = weakCardIds[0];
                const testCardProgress = await supabaseHelpers.getCardProgress(user.id, testCardId);

                // Also check if this card should still be in weak cards
                if (testCardProgress) {



                }
              }
            } catch (error) {
              console.error('Error getting weak cards:', error);

              filteredCards = flashcards;
            }
            break;
          case 'category':
            if (sessionConfig.category !== 'all') {

              const categoryMap: { [key: string]: string[] } = {
                'Contracts': ['Contracts'],
                'Georgia State Laws and Rules': ['Georgia State Laws and Rules'],
                'Property Ownership': ['Property Ownership'],
                'Agency': ['Agency'],
                'Financing': ['Financing'],
                'Valuation & Market Analysis': ['Valuation & Market Analysis'],
                'Georgia Real Estate Practice': ['Georgia Real Estate Practice'],
                'Finance and Closing': ['Finance and Closing'],
                'Transfer of Title': ['Transfer of Title'],
                'Leasing & Property Management': ['Leasing & Property Management'],
                'Real Estate Calculations': ['Real Estate Calculations'],
                'Practice of Real Estate': ['Practice of Real Estate'],
                'Property Disclosures': ['Property Disclosures'],
                'Land Use Controls': ['Land Use Controls']
              };
              const targetCategories = categoryMap[sessionConfig.category] || [sessionConfig.category];

              filteredCards = stateCards.filter(card => targetCategories.includes(card.category));


              
              // Debug: Show what categories were actually found

            }
            break;
          case 'random':
            if (sessionConfig.randomCount > 0) {

              const shuffled = [...stateCards].sort(() => Math.random() - 0.5);
              filteredCards = shuffled.slice(0, sessionConfig.randomCount);
            }
            break;
          default:




            break;
        }

        // Free users: max 5 cards per category (taste of each category, then show upgrade)
        if (userProfile?.subscription_type === 'free') {
          filteredCards = limitToFivePerCategory(filteredCards, FREE_CARDS_PER_CATEGORY);
        }

        // Apply card limit (but not for random, bookmarked, weak, or all sessions since they're already filtered)
        let limitedCards = filteredCards;
        if (sessionConfig.type !== 'random' && sessionConfig.type !== 'bookmarked' && sessionConfig.type !== 'weak' && sessionConfig.type !== 'all') {
          const effectiveCardLimit = sessionConfig.cardLimit > 0 ? sessionConfig.cardLimit : filteredCards.length;
          limitedCards = filteredCards.slice(0, effectiveCardLimit);
        }
        const shuffled = [...limitedCards].sort(() => Math.random() - 0.5);

        // Create new session in database
        try {
          const newSession = await supabaseHelpers.createStudySession({
            user_id: user.id,
            session_type: sessionConfig.type,
            session_config: sessionConfig,
            current_index: 0,
            shuffled_cards: shuffled.map(card => card.id),
            stats: {
              correct: 0,
              incorrect: 0,
              total: 0,
              streak: 0,
              maxStreak: 0
            },
            is_active: true
          });

          setCurrentSession(newSession);
        } catch (sessionError) {
          console.error('Failed to create session in database:', sessionError);

        }

        setShuffledCards(shuffled);
    setCurrentIndex(0);
    setStats({
      correct: 0,
      incorrect: 0,
      total: 0,
      streak: 0,
      maxStreak: 0
    });
        setIsLoadingCards(false);
        clearTimeout(loadingTimeout);

      } catch (error) {
        clearTimeout(loadingTimeout);
        console.error('Error initializing session:', error);
        // Fallback to basic session

        // Create a basic session without database
        let filteredCards = flashcards;
        
        // Apply basic filtering if possible
        if (sessionConfig.type === 'category' && sessionConfig.category !== 'all') {
          const categoryMap: { [key: string]: string[] } = {
            'Agency': ['Agency'],
            'Property Ownership': ['Property Ownership'],
            'Financing': ['Financing'],
            'Contracts': ['Contracts'],
            'Land Use Controls': ['Land Use Controls'],
            'Valuation': ['Valuation'],
            'Property Management': ['Property Management'],
            'Transfer of Title': ['Transfer of Title', 'Transfer ofTitle'],
            'Practice of Real Estate': ['Practice of Real Estate', 'Practice of Real-Estate', 'Practice of Real-Estat'],
            'Real Estate Calculations': ['Real Estate Calculations'],
            'Property Disclosures': ['Property Disclosures'],
            'Fair Housing': ['Fair Housing'],
            'Ethics': ['Ethics']
          };
          const targetCategories = categoryMap[sessionConfig.category] || [sessionConfig.category];
          filteredCards = flashcards.filter(card => targetCategories.includes(card.category));
        } else if (sessionConfig.type === 'random' && sessionConfig.randomCount > 0) {
    const shuffled = [...flashcards].sort(() => Math.random() - 0.5);
          filteredCards = shuffled.slice(0, sessionConfig.randomCount);
        }
        if (userProfile?.subscription_type === 'free') {
          filteredCards = limitToFivePerCategory(filteredCards, FREE_CARDS_PER_CATEGORY);
        }
        // Apply card limit (but not for random, bookmarked, weak, or all sessions since they're already filtered)
        let limitedCards = filteredCards;
        if (sessionConfig.type !== 'random' && sessionConfig.type !== 'bookmarked' && sessionConfig.type !== 'weak' && sessionConfig.type !== 'all') {
          const effectiveCardLimit = sessionConfig.cardLimit > 0 ? sessionConfig.cardLimit : filteredCards.length;
          limitedCards = filteredCards.slice(0, effectiveCardLimit);
        }
        const shuffled = [...limitedCards].sort(() => Math.random() - 0.5);
        
            setShuffledCards(shuffled);
        setCurrentIndex(0);
        setStats({
          correct: 0,
          incorrect: 0,
          total: 0,
          streak: 0,
          maxStreak: 0
        });
        setIsLoadingCards(false);
      }
    };

    initializeSession();
  }, [user, sessionConfig, mode, currentState, userProfile?.subscription_type]);



  // Save progress to Supabase
  const saveProgress = useCallback(async (newIndex: number, newStats: any) => {
    if (!currentSession || !user) {

      return;
    }

    try {
      await supabaseHelpers.updateStudySession(currentSession.id, {
        current_index: newIndex,
        stats: newStats,
        shuffled_cards: shuffledCards.map(card => card.id)
      });

    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentSession, user, shuffledCards]);

  // Check bookmark status
  const checkBookmarkStatus = useCallback(async (cardId: number) => {
    if (!user) return false;
    try {
      return await supabaseHelpers.isBookmarked(user.id, cardId);
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      return false;
    }
  }, [user]);

  // Toggle bookmark
  const toggleBookmark = useCallback(async () => {
    if (!user || !currentCard || mode === 'demo') return;
    
    try {
      if (isBookmarked) {
        await supabaseHelpers.removeBookmark(user.id, currentCard.id);
        setIsBookmarked(false);
      } else {
        await supabaseHelpers.addBookmark(user.id, currentCard.id);
        setIsBookmarked(true);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  }, [user, currentCard, isBookmarked, mode]);

  // Save stats to database and update card progress
  const saveStatsToDatabase = useCallback(async (newStats: { correct: boolean; streak: number }) => {
    if (user && userStats && currentCard) {
      try {
        // Update user stats
        const updatedStats = {
          ...userStats,
          questions_answered: userStats.questions_answered + 1,
          correct_answers: userStats.correct_answers + (newStats.correct ? 1 : 0),
          incorrect_answers: userStats.incorrect_answers + (newStats.correct ? 0 : 1),
          current_streak: newStats.streak,
          max_streak: Math.max(userStats.max_streak, newStats.streak),
          accuracy: userStats.questions_answered > 0 
            ? ((userStats.correct_answers + (newStats.correct ? 1 : 0)) / (userStats.questions_answered + 1)) * 100 
            : 0,
          progress: ((userStats.questions_answered + 1) / userStats.total_questions) * 100,
          daily_progress: userStats.daily_progress + 1,
          weekly_accuracy: userStats.weekly_accuracy
        };

        await updateUserStats(updatedStats);

        // Update card progress
        try {
          const existingProgress = await supabaseHelpers.getCardProgress(user.id, currentCard.id);
          const newTimesCorrect = (existingProgress?.times_correct || 0) + (newStats.correct ? 1 : 0);
          const newTimesIncorrect = (existingProgress?.times_incorrect || 0) + (newStats.correct ? 0 : 1);
          
          // Card is considered "known" if:
          // 1. User has answered it correctly more times than incorrectly, OR
          // 2. User answered it correctly this time (fresh success)
          const isKnown = newTimesCorrect > newTimesIncorrect || newStats.correct;
          
          const updatedProgress = {
            id: existingProgress?.id, // Include the existing record ID
            user_id: user.id,
            card_id: currentCard.id,
            times_correct: newTimesCorrect,
            times_incorrect: newTimesIncorrect,
            is_known: isKnown,
            last_answered: new Date().toISOString()
          };

          await supabaseHelpers.updateCardProgress(updatedProgress);

        } catch (cardError) {
          console.error('Error saving card progress:', cardError);
          // Continue without card progress
        }

      } catch (error) {
        console.error('Error saving stats to database:', error);
      }
    } else {

    }
  }, [user, userStats, updateUserStats, currentCard]);



  const toggleDarkMode = () => {
    if (mode === 'demo') {
      localStorage.setItem('demoDarkMode', JSON.stringify(!darkMode));
    }
    setDarkMode(!darkMode);
  };

  const handleSignOut = async () => {
    try {
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSwipe = useCallback(async (direction: 'left' | 'right') => {






    
    if (isAnimating || isProcessingSwipe) {

      return;
    }
    
    setIsProcessingSwipe(true);
    
    setIsAnimating(true);
    setSwipeDirection(direction);
    setShowAnswer(false);
    
    // Show banner
    setShowBanner(true);
    setBannerDirection(direction);
    
    // Update local stats with functional state update to prevent race conditions
    const isCorrect = direction === 'right';
    
    setStats(prevStats => {
      const newStats = {
        correct: prevStats.correct + (isCorrect ? 1 : 0),
        incorrect: prevStats.incorrect + (isCorrect ? 0 : 1),
        total: prevStats.total + 1,
        streak: isCorrect ? prevStats.streak + 1 : 0,
        maxStreak: isCorrect ? Math.max(prevStats.maxStreak, prevStats.streak + 1) : prevStats.maxStreak
      };

      // Mark that user has interacted with their first card
      if (!hasInteractedWithFirstCard) {
        setHasInteractedWithFirstCard(true);
      }

      // Save to database if user is authenticated
      saveStatsToDatabase({ correct: isCorrect, streak: newStats.streak });

      // Move to next card after animation completes
      setTimeout(async () => {
        const nextIndex = currentIndex + 1;




        // Check if user has reached the session limit
        if (nextIndex >= cardLimit) {
          // Deactivate session when complete
          if (currentSession) {
            try {
              await supabaseHelpers.deactivateStudySession(currentSession.id);
            } catch (error) {
              console.error('Error deactivating session:', error);
            }
          }
          
          // Redirect demo users directly to checkout
          if (mode === 'demo') {
            navigate('/checkout?plan=trial');
          } else {
            // For paid users, set index beyond available cards to trigger session completion screen
            setCurrentIndex(shuffledCards.length);
          }
          
          setIsAnimating(false);
          setSwipeDirection(null);
          setShowBanner(false);
          setBannerDirection(null);
          setIsProcessingSwipe(false);
        } else {
          // Save progress to localStorage with the new index
          await saveProgress(nextIndex, newStats);
          setCurrentIndex(nextIndex);
          setIsAnimating(false);
          setSwipeDirection(null);
          setShowBanner(false);
          setBannerDirection(null);
          setIsProcessingSwipe(false);
        }
      }, 600);

      return newStats;
    });
  }, [isAnimating, isProcessingSwipe, cardLimit, saveProgress, saveStatsToDatabase, currentSession, currentIndex, shuffledCards.length, mode, navigate, hasInteractedWithFirstCard]);

  const handleCardClick = useCallback(() => {

    setShowAnswer(!showAnswer);
  }, [showAnswer]);

  // Check bookmark status when card changes
  useEffect(() => {
    const updateBookmarkStatus = async () => {
      if (currentCard && user && mode !== 'demo') {
        const bookmarked = await checkBookmarkStatus(currentCard.id);
        setIsBookmarked(bookmarked);
      }
    };

    // Add a small delay to prevent rapid state changes during card transitions
    const timeoutId = setTimeout(updateBookmarkStatus, 100);
    return () => clearTimeout(timeoutId);
  }, [currentCard, user, checkBookmarkStatus, mode]);

  // Keyboard event handlers
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (isAnimating) return;

      switch (event.key) {
        case ' ':
          event.preventDefault();
          handleCardClick();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          handleSwipe('left');
          break;
        case 'ArrowRight':
          event.preventDefault();
          handleSwipe('right');
          break;
        case 'Escape':
          event.preventDefault();
          navigate('/dashboard');
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isAnimating, handleCardClick, handleSwipe, navigate]);

  const scrollToTourTarget = useCallback((id: string) => {
    const el = document.querySelector(`[data-tour="${id}"]`);
    if (el && el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }, []);

  useEffect(() => {
    if (!tourActive || tourStep >= FLASH_TOUR_STEPS.length) return;
    const step = FLASH_TOUR_STEPS[tourStep];
    scrollToTourTarget(step.id);
    const t = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-tour="${step.id}"]`);
      if (el && el instanceof HTMLElement) setTourRect(el.getBoundingClientRect());
      else setTourRect(null);
    });
    return () => cancelAnimationFrame(t);
  }, [tourActive, tourStep, scrollToTourTarget]);

  const handleTourNext = () => {
    if (tourStep >= FLASH_TOUR_STEPS.length - 1) setTourActive(false);
    else setTourStep(s => s + 1);
  };
  const handleTourBack = () => {
    if (tourStep <= 0) return;
    setTourStep(s => s - 1);
  };
  const handleTourSkip = () => setTourActive(false);

  // Show current session progress for the progress bar
  const sessionProgress = (currentIndex / cardLimit) * 100;

  // Debug function to check weak cards (available in console)
  if (typeof window !== 'undefined') {
    (window as any).checkWeakCards = async () => {
      if (!user) {

        return;
      }
      try {
        const weakCardIds = await supabaseHelpers.getWeakCards(user.id);

        for (const cardId of weakCardIds) {
          await supabaseHelpers.getCardProgress(user.id, cardId);

        }
      } catch (error) {
        console.error('Error checking weak cards:', error);
      }
    };

    // Debug function to test Supabase connection
    (window as any).testSupabaseConnection = async () => {
      if (!user) {

        return;
      }
      try {

        await supabase
          .from('user_card_progress')
          .select('count')
          .eq('user_id', user.id)
          .limit(1);

      } catch (error) {
        console.error('Connection test failed:', error);
      }
    };

    // Debug function to manually update a card's progress
    (window as any).updateCardProgress = async (cardId: number, correct: boolean) => {
      if (!user) {

        return;
      }
      try {

        const existingProgress = await supabaseHelpers.getCardProgress(user.id, cardId);

        const newTimesCorrect = (existingProgress?.times_correct || 0) + (correct ? 1 : 0);
        const newTimesIncorrect = (existingProgress?.times_incorrect || 0) + (correct ? 0 : 1);
        const isKnown = newTimesCorrect > newTimesIncorrect || correct;

        const updatedProgress = {
          id: existingProgress?.id, // Include the existing record ID
          user_id: user.id,
          card_id: cardId,
          times_correct: newTimesCorrect,
          times_incorrect: newTimesIncorrect,
          is_known: isKnown,
          last_answered: new Date().toISOString()
        };

        await supabaseHelpers.updateCardProgress(updatedProgress);

        // Check weak cards again
        await (window as any).checkWeakCards();
      } catch (error) {
        console.error('Error updating card progress:', error);
      }
    };
  }



  // When free user completes their 5-per-category session, show pricing modal once
  const hasAutoShownUpgradeRef = useRef(false);
  const onFreeUserCompletionScreen = !currentCard && shuffledCards.length > 0 && currentIndex >= cardLimit && isFreeAccount;
  useEffect(() => {
    if (onFreeUserCompletionScreen && !hasAutoShownUpgradeRef.current) {
      hasAutoShownUpgradeRef.current = true;
      setShowUpgradeModal(true);
    }
  }, [onFreeUserCompletionScreen]);

  // Show session completion screen only when we have cards but currentIndex is beyond the limit
  if (!currentCard && shuffledCards.length > 0 && currentIndex >= cardLimit) {
    return (
      <>
        <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
          darkMode 
            ? 'bg-gradient-to-br from-gray-900 to-gray-800' 
            : 'bg-gradient-to-br from-blue-400 to-purple-600'
        }`}>
          <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4 text-center`}>
            <div className="text-6xl mb-4">🎉</div>
            <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Great Job!</h1>
            <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {isFreeAccount
                ? "You've tried 5 cards from each category. Unlock full access to study unlimited cards."
                : "You've completed your study session!"}
            </p>
            
            {/* Performance Breakdown */}
            <div className={`mb-8 p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Session Performance</h2>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{stats.correct}</div>
                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Correct</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>{stats.incorrect}</div>
                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Incorrect</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>{stats.total}</div>
                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Total</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{stats.maxStreak}</div>
                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Best Streak</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                <div className={`text-lg font-semibold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                  Accuracy: {stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0}%
                </div>
              </div>
            </div>
          
            <div className="space-y-3">
              {isFreeAccount ? (
                <>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                  >
                    Unlock full access — 500+ cards
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-gray-200 text-gray-700 font-bold py-3 px-6 rounded-xl hover:bg-gray-300 transition-all duration-200"
                  >
                    Back to Dashboard
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-6 rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                  >
                    Start New Study Session
                  </button>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-gray-200 text-gray-700 font-bold py-3 px-6 rounded-xl hover:bg-gray-300 transition-all duration-200"
                  >
                    Back to Dashboard
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        {isFreeAccount && showUpgradeModal && (
          <SubscriptionModal
            darkMode={darkMode}
            onClose={() => setShowUpgradeModal(false)}
            onUpgrade={() => { setShowUpgradeModal(false); navigate('/checkout?plan=trial'); }}
          />
        )}
      </>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 overflow-x-hidden ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-900 to-gray-800' 
        : 'bg-gray-50'
    }`}>
      <Header 
        darkMode={darkMode} 
        onToggleDarkMode={toggleDarkMode} 
        userMode={mode} 
        onSignOut={handleSignOut}
        userProfile={userProfile}
      />
      <div className="relative flex-1 flex flex-col">
      <div className="flex-1 container mx-auto px-4 py-4 sm:py-6 lg:py-8 flex flex-col overflow-x-hidden" data-tour="flash-welcome">
        <div data-tour="flash-progress">
          <ProgressBar 
            progress={sessionProgress} 
            current={currentIndex + 1} 
            total={cardLimit} 
            darkMode={darkMode} 
          />
        </div>
        

        

        
        <div className="flex-1 flex justify-center items-center min-h-0 overflow-hidden relative px-2 sm:px-4 pb-8" data-tour="flash-card">


          {currentCard && shuffledCards.length > 0 ? (
          <FlashcardCard
            ref={cardRef}
            card={currentCard}
            showAnswer={showAnswer}
            onCardClick={handleCardClick}
            onSwipe={handleSwipe}
            isAnimating={isAnimating}
            swipeDirection={swipeDirection}
            showBanner={showBanner}
            bannerDirection={bannerDirection}
            darkMode={darkMode}
              isBookmarked={isBookmarked}
              onToggleBookmark={toggleBookmark}
            />
          ) : isLoadingCards ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className={`text-lg mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading your flashcards...</p>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                This may take a moment if you haven't used the app recently
              </p>
              <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                If this takes more than 10 seconds, please refresh the page
              </p>
            </div>
          ) : shuffledCards.length === 0 && (sessionConfig.type === 'weak' || sessionConfig.type === 'bookmarked') ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>You're All Caught Up!</h2>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                {sessionConfig.type === 'weak' 
                  ? "Great job! You've mastered all your weak cards."
                  : "Great job! You've mastered all your bookmarked cards."
                }
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
              >
                Back to Dashboard
              </button>
            </div>
          ) : shuffledCards.length === 0 && mode === 'demo' ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Demo Cards Not Available</h2>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                There was an issue loading the demo cards. Please try refreshing the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
              >
                Refresh Page
              </button>
            </div>
          ) : null}
        </div>

        {/* Fixed Footer Bar */}
        <div className="mt-auto pt-4 pb-2">
          <div className="flex justify-center space-x-3 sm:space-x-6 mb-4 sm:mb-6 lg:mb-8" data-tour="flash-buttons">
            {/* Previous Card Button */}
            {currentIndex > 0 && (
              <button
                onClick={() => {
                  if (!isAnimating && currentIndex > 0) {
                    setCurrentIndex(currentIndex - 1);
                    setShowAnswer(false);
                  }
                }}
                disabled={isAnimating || currentIndex === 0}
                className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-50 ${
                  darkMode 
                    ? 'bg-gray-600 text-white hover:bg-gray-500' 
                    : 'bg-gray-200/60 text-gray-500 hover:bg-gray-300/80 hover:text-gray-600'
                }`}
                title="Previous Card"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            
            <button
              onClick={() => handleSwipe('left')}
              disabled={isAnimating}
              className="w-12 h-12 sm:w-14 sm:h-14 bg-red-500 rounded-full flex items-center justify-center text-white text-lg sm:text-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-50"
            >
              ✕
            </button>
            
            <button
              onClick={() => handleSwipe('right')}
              disabled={isAnimating}
              className="w-12 h-12 sm:w-14 sm:h-14 bg-green-500 rounded-full flex items-center justify-center text-white text-lg sm:text-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-50"
            >
              ✓
            </button>
            
            {/* Info button – starts the tour */}
            <button
              onClick={() => { setTourActive(true); setTourStep(0); }}
              className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105 ${
                darkMode 
                  ? 'bg-gray-800/40 text-gray-400 hover:bg-gray-700/50 hover:text-gray-300' 
                  : 'bg-gray-50/60 text-gray-400 hover:bg-gray-100/70 hover:text-gray-600'
              }`}
              title="How to use flashcards (tour)"
              aria-label="Start flashcard tour"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </button>
          </div>

          <div data-tour="flash-stats">
            <Stats stats={stats} darkMode={darkMode} />
          </div>
        </div>
      </div>

      {/* Tour overlay with rounded cutout */}
      {tourActive && (
        <div ref={tourOverlayRef} className="fixed inset-0 z-40 pointer-events-none" aria-hidden>
          <svg className="absolute inset-0 w-full h-full" style={{ filter: 'drop-shadow(0 0 18px rgba(59, 130, 246, 0.4))' }}>
            <defs>
              <mask id="flash-tour-cutout">
                <rect width="100%" height="100%" fill="white" />
                {tourRect && (
                  <rect
                    x={tourRect.x - 10}
                    y={tourRect.y - 10}
                    width={tourRect.width + 20}
                    height={tourRect.height + 20}
                    rx="12"
                    ry="12"
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#flash-tour-cutout)" />
          </svg>
        </div>
      )}

      {/* Tour card and controls */}
      {tourActive && tourStep < FLASH_TOUR_STEPS.length && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full mx-4 pointer-events-auto">
          <div className={`rounded-xl shadow-xl border p-4 ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-slate-200'}`}>
            <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>{FLASH_TOUR_STEPS[tourStep].title}</h3>
            {FLASH_TOUR_STEPS[tourStep].id === 'flash-buttons' ? (
              <div className={`mt-3 space-y-3 text-sm ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 shadow ${darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200/80 text-gray-600'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                  </span>
                  <span>Previous card — go back to the last card.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-red-500 flex items-center justify-center shrink-0 text-white font-bold text-sm shadow">✕</span>
                  <span>Don&apos;t know — mark to review again.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center shrink-0 text-white font-bold text-sm shadow">✓</span>
                  <span>Know it — you got it right.</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 shadow ${darkMode ? 'bg-gray-700/80 text-gray-400' : 'bg-gray-50 text-gray-500 border border-gray-200'}`}>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
                  </span>
                  <span>Start this tour anytime.</span>
                </div>
                <p className="pt-1">You can also swipe left or right on the card.</p>
              </div>
            ) : (
              <p className={`mt-1 text-sm whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-slate-600'}`}>{FLASH_TOUR_STEPS[tourStep].body}</p>
            )}
            <div className="flex items-center justify-between mt-4 gap-3">
              <button type="button" onClick={handleTourSkip} className={`text-sm font-medium ${darkMode ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'}`}>Skip</button>
              <div className="flex gap-2">
                <button type="button" onClick={handleTourBack} disabled={tourStep === 0} className={`px-3 py-1.5 rounded-lg text-sm font-medium ${darkMode ? 'bg-gray-700 text-gray-200 disabled:opacity-50' : 'bg-slate-100 text-slate-700 disabled:opacity-50'}`}>Back</button>
                <button type="button" onClick={handleTourNext} className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700">Next</button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  How to Use Flashcards
                </h3>
                <button
                  onClick={() => setShowInstructions(false)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    darkMode 
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Instructions */}
              <div className={`space-y-8 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {/* Card Interaction */}
                <div>
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74l-3.43-.72c-.08-.01-.15-.03-.24-.03-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.44 1.06.44h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.91-1.38z"/>
                      </svg>
                    </div>
                  </div>
                  <h4 className="font-semibold mb-2 text-lg">Tap the card</h4>
                  <p className="text-sm">Click anywhere on the card to flip it and see the answer.</p>
                </div>

                {/* Answer Methods */}
                <div>
                  <h4 className="font-semibold mb-4 text-lg">Answer the card</h4>
                  <div className="space-y-4">
                    <div className="text-sm">
                      <span className="font-medium">Swipe left/right</span>
                      <span className="text-gray-500 mx-2">or</span>
                      <span className="font-medium">use buttons</span>
                    </div>
                    <div className="flex justify-center space-x-8">
                      <div className="text-center">
                        <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-2">
                          ✕
                        </div>
                        <div className="text-xs text-gray-500">Don't know</div>
                      </div>
                      <div className="text-center">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-2">
                          ✓
                        </div>
                        <div className="text-xs text-gray-500">Know</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bookmark */}
                <div>
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
                      </svg>
                    </div>
                  </div>
                  <h4 className="font-semibold mb-2 text-lg">Bookmark cards</h4>
                  <p className="text-sm">Save difficult cards for later review from your dashboard.</p>
                </div>
              </div>

              {/* Tips */}
              <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                <h4 className={`font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  💡 Tips
                </h4>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li>• Take your time to read each question</li>
                  <li>• Review explanations to understand the reasoning</li>
                  <li>• Bookmark difficult cards for focused review</li>
                </ul>
              </div>

              {/* Close Button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowInstructions(false)}
                  className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                    darkMode 
                      ? 'bg-gray-700 text-white hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FlashcardApp;
